import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "../../lib/api";
import { downloadBlob } from "../../lib/download";
import { toast } from "sonner";
import { Plus, Check, X, Archive, Pencil, ArchiveRestore, Users, FileArchive, Loader2, Link2, Copy, History, ShieldCheck, Lock, Unlock } from "lucide-react";

const Dot = ({ ok }) => (
  <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: ok ? "#10B981" : "#9CA3AF" }}>
    {ok ? <Check size={15} /> : <X size={15} />}
  </span>
);

const ROLE_ROWS = [
  ["manager", "Manager access", "manager_configured"],
  ["worker", "Team access (shared worker login)", "worker_configured"],
  ["review", "Review access", "review_configured"],
];

async function copySetupLink(url) {
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied — paste it into your email to the client");
  } catch {
    toast.error("Copy blocked — select the link text and copy manually");
  }
}

function SetupLinkPanel({ role, link }) {
  return (
    <div className="mt-2" data-testid={`setup-link-panel-${role}`}>
      <div className="flex gap-2">
        <input data-testid={`setup-link-value-${role}`} readOnly value={link.url}
          onFocus={(e) => e.target.select()}
          className="flex-1 rounded-lg px-2.5 py-2 text-xs border font-mono" style={{ borderColor: "#E5E7EB", backgroundColor: "#F9FAFB" }} />
        <button data-testid={`copy-link-${role}`} onClick={() => copySetupLink(link.url)}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white" style={{ backgroundColor: "#F97316" }}>
          <Copy size={13} /> Copy
        </button>
      </div>
      <p className="text-[11px] text-[#6B7280] mt-1.5">
        One-time link — expires {new Date(link.expires).toLocaleDateString()}. The recipient sets their own password; you never see it.
      </p>
    </div>
  );
}

function AccessLinkRow({ client, role, label, configured }) {
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/setup-links`, { role });
      setLink({ url: `${window.location.origin}/setup/${data.token}`, expires: data.expires_at });
    } catch {
      toast.error("Could not generate link");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border p-3" style={{ borderColor: "#E5E7EB" }} data-testid={`access-row-${role}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium flex items-center gap-2">
          {label}
          <Dot ok={configured} />
          <span className="text-xs text-[#9CA3AF]">{configured ? "password set" : "no password yet"}</span>
        </span>
        <button data-testid={`generate-link-${role}`} onClick={generate} disabled={busy}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60"
          style={{ backgroundColor: "#0A1F44" }}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}
          {configured ? "Generate reset link" : "Generate setup link"}
        </button>
      </div>
      {link && <SetupLinkPanel role={role} link={link} />}
    </div>
  );
}

const FEATURE_ROWS = [
  ["implementation", "Implementation Centre"],
  ["account", "Billing"],
];

function toggleIcon(busy, unlocked) {
  if (busy) return <Loader2 size={13} className="animate-spin" />;
  return unlocked ? <Lock size={13} /> : <Unlock size={13} />;
}

function FeatureToggleRow({ client, feature, label, initialUnlocked }) {
  const [unlocked, setUnlocked] = useState(initialUnlocked);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setBusy(true);
    try {
      const next = !unlocked;
      await api.put(`/clients/${client.id}/features/${feature}`, { unlocked: next });
      setUnlocked(next);
      toast.success(next ? `${label} unlocked — now visible to the client` : `${label} locked — hidden from the client`);
    } catch {
      toast.error("Could not update feature");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border p-3" style={{ borderColor: "#E5E7EB" }} data-testid={`feature-row-${feature}`}>
      <span className="text-sm font-medium flex items-center gap-2">
        {label}
        <span data-testid={`feature-state-${feature}`} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
          style={unlocked ? { backgroundColor: "#DCFCE7", color: "#10B981" } : { backgroundColor: "#F3F4F6", color: "#6B7280" }}>
          {unlocked ? <Unlock size={11} /> : <Lock size={11} />}
          {unlocked ? "Unlocked" : "Locked"}
        </span>
      </span>
      <button data-testid={`feature-toggle-${feature}`} onClick={toggle} disabled={busy}
        className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60"
        style={{ backgroundColor: unlocked ? "#6B7280" : "#F97316" }}>
        {toggleIcon(busy, unlocked)}
        {unlocked ? "Lock" : "Unlock"}
      </button>
    </div>
  );
}

function EditClientSections({ client }) {
  return (
    <div className="space-y-2 pt-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">Portal access</p>
      <p className="text-xs text-[#6B7280] leading-relaxed">
        Clients set their own passwords via one-time setup links. Generate a link, copy it into your email.
        Generating a new link is also the password reset path.
      </p>
      {ROLE_ROWS.map(([role, label, flag]) => (
        <AccessLinkRow key={role} client={client} role={role} label={label} configured={client[flag]} />
      ))}
      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] pt-2">Features</p>
      <p className="text-xs text-[#6B7280] leading-relaxed">
        Locked features are completely invisible in the client's portals — build them privately, unlock when ready.
        You always have full access in admin. Every lock and unlock is logged.
      </p>
      {FEATURE_ROWS.map(([feature, label]) => (
        <FeatureToggleRow key={feature} client={client} feature={feature} label={label}
          initialUnlocked={!!client.features_unlocked?.[feature]} />
      ))}
    </div>
  );
}

const modalField = "w-full rounded-lg px-3 py-2.5 outline-none border focus:ring-2 focus:ring-[#F97316]";

function ClientModalBody({ editing, initial, name, setName }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="text-sm text-[#6B7280] mb-1 block">Client name</label>
        <input data-testid="client-name-input" value={name} onChange={(e) => setName(e.target.value)} className={modalField} style={{ borderColor: "#E5E7EB" }} />
      </div>
      {editing ? (
        <EditClientSections client={initial} />
      ) : (
        <p className="text-xs text-[#6B7280] leading-relaxed">
          After saving, open <span className="font-semibold">Edit</span> on the client to generate password setup links for each access level.
        </p>
      )}
    </div>
  );
}

function ClientModal({ initial, onClose, onSaved }) {
  const editing = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Client name required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/clients/${initial.id}`, { name });
        toast.success("Client updated");
      } else {
        await api.post("/clients", { name });
        toast.success("Client created — now open Edit to generate access links");
      }
      onSaved();
      onClose();
    } catch {
      toast.error("Failed to save client");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 py-10 overflow-y-auto" style={{ backgroundColor: "rgba(10,31,68,0.5)" }}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 border my-auto" style={{ borderColor: "#E5E7EB" }} data-testid="client-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-bold">{editing ? "Edit Client" : "New Client"}</h3>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#1A1A1A]"><X size={20} /></button>
        </div>
        <ClientModalBody editing={editing} initial={initial} name={name} setName={setName} />
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 rounded-lg px-4 py-2.5 font-medium border" style={{ borderColor: "#E5E7EB" }}>Cancel</button>
          <button data-testid="client-save-button" onClick={save} disabled={busy}
            className="flex-1 rounded-lg px-4 py-2.5 font-semibold text-white disabled:opacity-60" style={{ backgroundColor: "#F97316" }}>
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

const EVIDENCE_LABELS = {
  login: "Logged in",
  password_set: "Password set via setup link",
  approval: "Approved document",
  feature_unlock: "Feature unlocked",
  feature_lock: "Feature locked",
  window_opened: "Review window opened",
  window_closed: "Review window closed",
  account_approval: "Approved account item",
  account_decline: "Declined account item",
};

function EvidenceItem({ e }) {
  return (
    <div data-testid={`evidence-item-${e.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 border-b last:border-b-0 text-sm" style={{ borderColor: "#F3F4F6" }}>
      <div className="min-w-0">
        <p className="font-medium truncate">
          {EVIDENCE_LABELS[e.event] || e.event}
          {e.meta?.document_title && <span className="text-[#6B7280]"> — {e.meta.document_title}</span>}
          {e.meta?.feature && <span className="text-[#6B7280]"> — {e.meta.feature === "implementation" ? "Implementation Centre" : "Billing"}</span>}
          {e.meta?.category && <span className="text-[#6B7280]"> — {e.meta.category}</span>}
        </p>
        <p className="text-xs text-[#6B7280]">{e.role} · IP {e.ip || "unknown"}</p>
      </div>
      <span className="text-xs text-[#6B7280] flex-shrink-0">{new Date(e.created_at).toLocaleString()}</span>
    </div>
  );
}

function EvidenceModal({ client, onClose }) {
  const [entries, setEntries] = useState(null);

  useEffect(() => {
    api.get(`/clients/${client.id}/evidence-log`).then(({ data }) => setEntries(data));
  }, [client.id]);

  const renderEntries = () => {
    if (entries === null) return <p className="text-sm text-[#9CA3AF] px-4 py-6 text-center">Loading...</p>;
    if (entries.length === 0) return <p data-testid="evidence-empty" className="text-sm text-[#9CA3AF] px-4 py-6 text-center">No events recorded yet.</p>;
    return entries.map((e) => <EvidenceItem key={e.id} e={e} />);
  };

  return (
    <div data-testid="evidence-modal" className="fixed inset-0 z-[70] flex items-start justify-center p-4 py-10 overflow-y-auto" style={{ backgroundColor: "rgba(10,31,68,0.6)" }}>
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 border my-auto" style={{ borderColor: "#E5E7EB" }}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-xl font-bold flex items-center gap-2">
            <ShieldCheck size={20} style={{ color: "#F97316" }} /> Security & Evidence Log
          </h3>
          <button data-testid="evidence-close" onClick={onClose} className="text-[#6B7280] hover:text-[#1A1A1A]"><X size={20} /></button>
        </div>
        <p className="text-sm text-[#6B7280] mb-4">
          {client.name} — every login, password-set and approval, with IP address and timestamp. Included in the audit export.
        </p>
        <div className="rounded-lg border max-h-[28rem] overflow-y-auto" style={{ borderColor: "#E5E7EB" }} data-testid="evidence-list">
          {renderEntries()}
        </div>
      </div>
    </div>
  );
}

const EXPORT_SCOPES = [
  ["full", "Full export"],
  ["document", "Single document"],
  ["decisions", "Decisions only"],
  ["feedback", "Feedback only"],
  ["implementation", "Implementation only"],
];

function useAuditExport(client, onClose) {
  const [exporting, setExporting] = useState(false);

  const runExport = async (scope, docId) => {
    if (scope === "document" && !docId) { toast.error("Select a document to export"); return; }
    setExporting(true);
    try {
      const res = await api.post(`/clients/${client.id}/audit-export`,
        { scope, document_id: scope === "document" ? docId : undefined }, { responseType: "blob" });
      const m = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/);
      downloadBlob(res.data, m ? m[1] : `${client.name}_AuditExport.zip`, "Audit package ready");
      onClose();
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return { exporting, runExport };
}

function ExportHistoryList({ history }) {
  const renderItems = () => {
    if (history === null) return <p className="text-xs text-[#9CA3AF] px-3 py-2.5">Loading...</p>;
    if (history.length === 0) return <p data-testid="export-history-empty" className="text-xs text-[#9CA3AF] px-3 py-2.5">No exports generated yet.</p>;
    return history.map((h) => (
      <div key={h.id} data-testid={`export-history-item-${h.id}`} className="flex items-center justify-between gap-2 px-3 py-2 border-b last:border-b-0 text-xs" style={{ borderColor: "#F3F4F6" }}>
        <span className="text-[#1A1A1A] font-medium truncate">{h.scope_label}</span>
        <span className="text-[#6B7280] flex-shrink-0">{new Date(h.created_at).toLocaleString()}</span>
      </div>
    ));
  };

  return (
    <div className="mt-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-1.5">Export history</p>
      <div data-testid="export-history" className="rounded-lg border max-h-36 overflow-y-auto" style={{ borderColor: "#E5E7EB" }}>
        {renderItems()}
      </div>
    </div>
  );
}

const EXPORT_FIELD_CLS = "w-full rounded-lg px-3 py-2.5 outline-none border focus:ring-2 focus:ring-[#F97316] text-sm";

function ExportScopeFields({ scope, setScope, docId, setDocId, docs }) {
  return (
    <>
      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-1.5 block">Export scope</label>
        <select data-testid="export-scope-select" value={scope} onChange={(e) => setScope(e.target.value)} className={EXPORT_FIELD_CLS} style={{ borderColor: "#E5E7EB" }}>
          {EXPORT_SCOPES.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
        </select>
      </div>
      {scope === "document" && (
        <div className="mt-3">
          <label className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-1.5 block">Document</label>
          <select data-testid="export-doc-select" value={docId} onChange={(e) => setDocId(e.target.value)} className={EXPORT_FIELD_CLS} style={{ borderColor: "#E5E7EB" }}>
            <option value="">Select a document...</option>
            {docs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </div>
      )}
    </>
  );
}

function ExportModal({ client, onClose }) {
  const [scope, setScope] = useState("full");
  const [docId, setDocId] = useState("");
  const [docs, setDocs] = useState([]);
  const [history, setHistory] = useState(null);
  const { exporting, runExport } = useAuditExport(client, onClose);

  useEffect(() => {
    api.get(`/clients/${client.id}/documents`).then(({ data }) => setDocs(data));
    api.get(`/clients/${client.id}/export-logs`).then(({ data }) => setHistory(data));
  }, [client.id]);

  return createPortal(
    <div data-testid="export-modal" className="fixed inset-0 z-[70] flex items-start justify-center p-4 py-10 overflow-y-auto" style={{ backgroundColor: "rgba(10,31,68,0.6)" }}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 border my-auto" style={{ borderColor: "#E5E7EB" }}>
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: "#FEF0E6" }}>
          <FileArchive size={22} style={{ color: "#F97316" }} />
        </div>
        <h3 className="font-heading text-xl font-bold mb-2">Export Audit Package</h3>
        <p className="text-sm text-[#6B7280] leading-relaxed">
          Generate a time-stamped ZIP (audit report PDF, raw JSON data, and attachments) for <span className="font-semibold text-[#1A1A1A]">{client.name}</span>.
        </p>

        <ExportScopeFields scope={scope} setScope={setScope} docId={docId} setDocId={setDocId} docs={docs} />

        <ExportHistoryList history={history} />

        <div className="flex gap-3 mt-6">
          <button data-testid="export-cancel" onClick={onClose} disabled={exporting}
            className="flex-1 rounded-lg px-4 py-2.5 font-medium border" style={{ borderColor: "#E5E7EB" }}>Cancel</button>
          <button data-testid="export-confirm" onClick={() => runExport(scope, docId)} disabled={exporting}
            className="flex-1 rounded-lg px-4 py-2.5 font-semibold text-white disabled:opacity-60 flex items-center justify-center gap-2" style={{ backgroundColor: "#F97316" }}>
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <FileArchive size={16} />}
            {exporting ? "Generating…" : "Export Audit Package"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ClientRow({ c, striped, onExport, onLog, onEdit, onArchive }) {
  return (
    <tr data-testid={`client-row-${c.id}`} style={{ backgroundColor: striped ? "#F9FAFB" : "#fff", opacity: c.archived ? 0.55 : 1 }}>
      <td className="px-5 py-3 font-medium">
        {c.name} {c.archived && <span className="text-xs text-[#6B7280]">(archived)</span>}
      </td>
      <td className="text-center px-3 py-3"><div className="flex justify-center"><Dot ok={c.manager_configured} /></div></td>
      <td className="text-center px-3 py-3"><div className="flex justify-center"><Dot ok={c.worker_configured} /></div></td>
      <td className="text-center px-3 py-3"><div className="flex justify-center"><Dot ok={c.review_configured} /></div></td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-1">
          <button data-testid={`export-client-${c.id}`} onClick={onExport} title="Export Audit Package"
            className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg text-white" style={{ backgroundColor: "#0A1F44" }}>
            <FileArchive size={14} /> Export Audit
          </button>
          <button data-testid={`evidence-client-${c.id}`} onClick={onLog} title="Security & evidence log"
            className="p-2 rounded-lg hover:bg-gray-100 text-[#6B7280]"><History size={15} /></button>
          <button data-testid={`edit-client-${c.id}`} onClick={onEdit} className="p-2 rounded-lg hover:bg-gray-100 text-[#6B7280]"><Pencil size={15} /></button>
          <button data-testid={`archive-client-${c.id}`} onClick={onArchive} className="p-2 rounded-lg hover:bg-gray-100 text-[#6B7280]">
            {c.archived ? <ArchiveRestore size={15} /> : <Archive size={15} />}
          </button>
        </div>
      </td>
    </tr>
  );
}

function ClientsTable({ clients, setExportTarget, setLogTarget, setModal, archive }) {
  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: "#E5E7EB" }}>
      <table className="w-full text-sm">
        <thead>
          <tr style={{ backgroundColor: "#0A1F44", color: "#fff" }}>
            <th className="text-left font-medium px-5 py-3">Client</th>
            <th className="text-center font-medium px-3 py-3">Manager</th>
            <th className="text-center font-medium px-3 py-3">Worker</th>
            <th className="text-center font-medium px-3 py-3">Review</th>
            <th className="text-right font-medium px-5 py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {clients.length === 0 ? (
            <tr><td colSpan={5} className="text-center py-12 text-[#6B7280]">
              <Users size={32} className="mx-auto mb-2 opacity-40" />No clients yet. Create your first one.</td></tr>
          ) : clients.map((c, i) => (
            <ClientRow key={c.id} c={c} striped={i % 2 === 1}
              onExport={() => setExportTarget(c)} onLog={() => setLogTarget(c)}
              onEdit={() => setModal(c)} onArchive={() => archive(c)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [modal, setModal] = useState(null); // {} for new, client for edit
  const [exportTarget, setExportTarget] = useState(null);
  const [logTarget, setLogTarget] = useState(null);

  const load = useCallback(() => api.get("/clients")
    .then(({ data }) => setClients(data))
    .catch(() => {
      // keep previously loaded data; only warn when there's nothing to show
      setClients((prev) => {
        if (!prev.length) toast.error("Couldn't load clients — check your connection and try again");
        return prev;
      });
    }), []);
  useEffect(() => { load(); }, [load]);

  const archive = async (c) => {
    await api.post(`/clients/${c.id}/archive`);
    toast.success(c.archived ? "Client restored" : "Client archived");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold">Clients</h1>
          <p className="text-[#6B7280] text-sm mt-1">Manage farm business clients and portal passwords.</p>
        </div>
        <button data-testid="add-client-button" onClick={() => setModal({})}
          className="flex items-center gap-2 rounded-lg px-4 py-2.5 font-semibold text-white" style={{ backgroundColor: "#F97316" }}>
          <Plus size={18} /> New Client
        </button>
      </div>

      <ClientsTable clients={clients} setExportTarget={setExportTarget} setLogTarget={setLogTarget}
        setModal={setModal} archive={archive} />

      {modal && <ClientModal initial={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={load} />}

      {logTarget && <EvidenceModal client={logTarget} onClose={() => setLogTarget(null)} />}

      {exportTarget && <ExportModal client={exportTarget} onClose={() => setExportTarget(null)} />}
    </div>
  );
}
